import { useState } from "haunted";
import { html } from "lit-html";
import * as dayjs from "dayjs";
import * as ObjectSupport from "dayjs/plugin/objectSupport";
import * as IsSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as IsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as Weekday from "dayjs/plugin/weekday";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import classNames from "classnames";
import { TestIdDictionary as T } from "Services/test-ids/TestIdDictionary";
import { COMMON_DAYJS_FORMAT } from "Services/constants";
import { useEffectWithoutCleanup, useMemo } from "Shared/haunted/CustomHooks";
import { capitalizeAndRemoveDot } from "Services/common";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import { Schedule } from "Shared/models/Schedule";
import { calendarUtils } from "./calendarUtils";
import { useNewFlightDateCalendar } from "./useNewFlightDateCalendar";
dayjs.extend(CustomParseFormat);
dayjs.extend(ObjectSupport);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsSameOrAfter);
dayjs.extend(Weekday);

export interface Props {
	availableSchedules: Schedule[];
	culture: string;
	disabledDates: dayjs.Dayjs[];
	max: dayjs.Dayjs;
	min: dayjs.Dayjs;
	rangeStart: dayjs.Dayjs;
	usePrices: boolean;
	selectedDate: dayjs.Dayjs;
	onChange: (date: dayjs.Dayjs) => void;
}

export const useNewFlightDatePickerDropdown = (props: Props) => {
	const windowSize = useWindowSize();

	const [currentDate, setCurrentDate] = useState<dayjs.Dayjs>(props.min || dayjs());

	// Calendar utilities
	const { getMonthCalendar } = calendarUtils();

	const move = (direction: number) => setCurrentDate(currentDate.add(direction, "month"));

	const isBackOneMonthDisabled = () =>
		props.min?.year() === currentDate.year() && props.min.month() >= currentDate.month();

	const isForwardOneMonthDisabled = () =>
		props.max?.year() === currentDate.year() && props.max.month() <= currentDate.month();

	const lowestDisplayedPrice = useMemo((): number => {
		if (!props.availableSchedules?.length) return 0;

		const firstMonthDates = getMonthCalendar(currentDate, props.culture)[0];

		const secondMonthDates = getMonthCalendar(currentDate.add(1, "month"), props.culture)[0];

		const displayedDates = firstMonthDates.concat(windowSize.size !== "XS" ? secondMonthDates : []) || [];

		const displayedSchedules = props.availableSchedules.filter((schedule) =>
			displayedDates.some((date) => schedule.departureDate.isSame(date, "date"))
		);

		const minimumPriceSchedule = displayedSchedules.reduce((acc, curr) => {
			if (acc && acc.price < curr.price) return acc;
			return curr;
		}, undefined as Schedule);

		return minimumPriceSchedule?.price || 0;
	}, [props.availableSchedules, currentDate, windowSize.size]);

	// EVENT HANDLERS

	const handleBack = (e: MouseEvent) => {
		e.stopPropagation();
		move(-1);
	};

	const handleForward = (e: MouseEvent) => {
		e.stopPropagation();
		move(1);
	};

	const handleDateClick = (day: dayjs.Dayjs, e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		props.onChange(day);
	};

	useEffectWithoutCleanup(() => {
		if (!props.selectedDate && !props.rangeStart) return;
		setCurrentDate(dayjs(props.selectedDate || props.rangeStart));
	}, [props.selectedDate?.format(COMMON_DAYJS_FORMAT), props.rangeStart]);

	useEffectWithoutCleanup(() => props.min && setCurrentDate(props.min), [props.min?.format(COMMON_DAYJS_FORMAT)]);

	const calendarForMonth = useNewFlightDateCalendar({
		availableSchedules: props.availableSchedules,
		culture: props.culture,
		disabledDates: props.disabledDates,
		max: props.max,
		min: props.min,
		rangeStart: props.rangeStart,
		usePrices: props.usePrices,
		selectedDate: props.selectedDate,
		lowestDisplayedPrice,
		onDateClick: handleDateClick,
	});

	// TEMPLATES

	const forwardOneMonthTemplate = (testId: string, hiddenAboveMobile = false) => html`
		<div
			class=${classNames("flex h-full w-full cursor-pointer items-center justify-end", "md:hover:text-n-cyan", {
				"pointer-events-none opacity-0":
					isForwardOneMonthDisabled() || (hiddenAboveMobile && windowSize.size !== "XS"),
			})}
			data-test-id=${testId}
			@click=${handleForward}
		>
			<i class="jsh-chevron-right1"></i>
		</div>
	`;

	const backOneMonthTemplate = (testId: string, hiddenOnDesktop = false) => html`
		<div
			class=${classNames("flex h-full w-full  cursor-pointer items-center", "md:hover:text-n-cyan", {
				"pointer-events-none opacity-0":
					isBackOneMonthDisabled() || (hiddenOnDesktop && windowSize.size !== "XS"),
			})}
			data-test-id=${testId}
			@click=${handleBack}
		>
			<i class="jsh-chevron-right1 rotate-180 transform"></i>
		</div>
	`;

	const firstMonthNavigationTemplate = () => html`
		<div class=${classNames("grid w-[280px] grid-cols-[1fr_3fr_1fr] items-center", "lg:w-[378px]")}>
			${backOneMonthTemplate(T.DATE.FIRST_MONTH_NAVIGATION_MOVE_BACK)}
			<div
				class="whitespace-nowrap text-center font-emphasis text-xl/none text-n-blue"
				data-test-id=${T.DATE.FIRST_MONTH_NAVIGATION_NAME}
				data-test-value=${currentDate.format("YYYY-MM")}
			>
				${capitalizeAndRemoveDot(dayjs(currentDate).format("MMMM YYYY"))}
			</div>
			${forwardOneMonthTemplate(T.DATE.FIRST_MONTH_NAVIGATION_MOVE_FORWARD, true)}
		</div>
	`;

	const secondMonthNavigationTemplate = () => {
		const dateToDisplay = dayjs(currentDate).add(1, "month");

		return html`
			<div class=${classNames("grid w-[280px] grid-cols-[1fr_3fr_1fr] items-center", "lg:w-[378px]")}>
				${backOneMonthTemplate(T.DATE.SECOND_MONTH_NAVIGATION_MOVE_BACK, true)}
				<div
					class="whitespace-nowrap text-center font-emphasis text-xl/none text-n-blue"
					data-test-id=${T.DATE.SECOND_MONTH_NAVIGATION_NAME}
					data-test-value=${dateToDisplay.format("YYYY-MM")}
				>
					${capitalizeAndRemoveDot(dateToDisplay.format("MMMM YYYY"))}
				</div>
				${forwardOneMonthTemplate(T.DATE.SECOND_MONTH_NAVIGATION_MOVE_FORWARD)}
			</div>
		`;
	};

	const newMonthCalendarFirstMonthTemplate = () => {
		const calendarMonth = getMonthCalendar(currentDate, props.culture);
		return calendarForMonth.getCalendarForMonth(calendarMonth, false);
	};

	const newMonthCalendarSecondMonthTemplate = () => {
		const calendarMonth = getMonthCalendar(currentDate.add(1, "month"), props.culture);
		return calendarForMonth.getCalendarForMonth(calendarMonth, false);
	};

	const htmlTemplate = () => html`
		<div class="relative flex h-full w-full bg-white">
			<div
				class=${classNames("relative h-full w-full px-5 py-3")}
				data-test-id=${T.DATE.FIRST_MONTH_NAVIGATION_CONTAINER}
			>
				${firstMonthNavigationTemplate()} ${newMonthCalendarFirstMonthTemplate()}
			</div>
			<div
				class=${classNames("relative h-full w-1/2 px-5 py-3 hidden", "sm:block")}
				data-test-id=${T.DATE.SECOND_MONTH_NAVIGATION_CONTAINER}
			>
				${secondMonthNavigationTemplate()} ${newMonthCalendarSecondMonthTemplate()}
			</div>
		</div>
	`;
	return { htmlTemplate };
};
